import type Decimal from 'decimal.js';
import { type StateRule, type StateRuleState, stateOn } from '../catalogue/state-rules.js';
import type { Flag } from '../commands/envelope.js';
import { D } from '../internal/decimal.js';
import { qty, qtyToString } from '../quantity/index.js';
import { type Instant, type PlainDate, localDate, toDate } from '../time/index.js';
import { type Uom, type UomConversion, convert } from '../uom/index.js';

/** Location types (plan §5.1). */
export const LOCATION_TYPES = [
  'shelf',
  'back_store',
  'production',
  'cold_store',
  'marketplace_warehouse',
  'third_party_warehouse',
  'in_transit',
  'held_for_customer',
  'quarantine',
] as const;
/** One of {@link LOCATION_TYPES}. */
export type LocationType = (typeof LOCATION_TYPES)[number];

/** What a location may feed: production consumption, sales, both or neither. */
export interface LocationUse {
  feedsProduction: boolean;
  feedsSale: boolean;
}

/**
 * Default use per location type, until an account configures its own: production, cold
 * store and back store feed production; the shelf feeds sales; stock in transit, held for
 * a customer, in quarantine or in someone else's warehouse feeds neither.
 */
export function defaultLocationUse(type: LocationType): LocationUse {
  switch (type) {
    case 'production':
    case 'cold_store':
    case 'back_store':
      return { feedsProduction: true, feedsSale: false };
    case 'shelf':
      return { feedsProduction: false, feedsSale: true };
    default:
      return { feedsProduction: false, feedsSale: false };
  }
}

/** A claim on part of a lot row (e.g. batch output claimed for an order). */
export interface LotReservation {
  reservationId: string;
  /** Decimal string ≥ 0, in the product's base unit. */
  qty: string;
  /** The order line the stock is claimed for, if any (named in `reservation_broken`). */
  orderLineId?: string | null;
}

/**
 * One stock row FEFO can draw from: a lot at a location in a recorded state. The same lot
 * at two locations (or in two states) is two rows.
 */
export interface FefoLot {
  lotId: string;
  productId: string;
  locationId: string;
  /** The state the stock is recorded in (its balance row). */
  state: string;
  /** On hand, in the product's base unit; may be negative (sold beyond stock). */
  qty: string;
  /** `null` for stock that does not track expiry. */
  bestBefore: PlainDate | null;
  /** ISO instant; `null` when unknown. */
  producedAt: string | null;
  reservations?: readonly LotReservation[];
}

/** What the stock is for: production consumption or a sale. */
export type FefoPurpose = 'consumption' | 'sale';

/** A request to allocate stock of one product. */
export interface FefoRequest {
  productId: string;
  /** Decimal string in `uom`. Zero or negative → nothing allocated. */
  qty: string;
  uom: Uom;
  purpose: FefoPurpose;
  /** When the stock is used (batch start, sale time). Best-before is judged on its local date. */
  useAt: Instant;
  /** Claims with this id are this request's own: available to it, and drawn first. */
  reservationId?: string;
}

/** Everything else FEFO needs to know. */
export interface FefoContext {
  /** The account's IANA zone; best-before is compared with the **local** use date. */
  timeZone: string;
  /** The product's base unit (lot quantities are in it). */
  baseUom: Uom;
  /** The product's state rule. */
  stateRule: StateRule;
  /** Use per location id. A location not listed is not eligible. */
  locations: ReadonlyMap<string, LocationUse>;
  conversions?: readonly UomConversion[];
}

/** Why a row was not drawn from. */
export type FefoSkipReason =
  | 'other_product'
  | 'location_unknown'
  | 'location_not_allowed'
  | 'expires_before_use'
  | 'state_unknown'
  | 'state_not_usable'
  | 'nothing_available';

/** A row that was not eligible, and why. */
export interface FefoSkip {
  lotId: string;
  locationId: string;
  state: string;
  reason: FefoSkipReason;
}

/** Stock taken from one row. */
export interface FefoAllocation {
  lotId: string;
  locationId: string;
  /** The recorded state to move the stock out of. */
  state: string;
  bestBefore: PlainDate | null;
  /** Allocated from this row, in the base unit (> 0). */
  qty: string;
  /** Part of `qty` that came from the request's own reservation. */
  fromReservation: string;
}

/** Result of {@link allocateFefo}. */
export interface FefoResult {
  /** The request in the base unit; `null` when it could not be converted. */
  requested: string | null;
  /** Σ allocations. Equals min(requested, eligible available) unless selling beyond stock. */
  allocated: string;
  /** Unit of `requested`, `allocated` and every allocation. */
  uom: Uom;
  /** In draw order. */
  allocations: FefoAllocation[];
  skipped: FefoSkip[];
  /**
   * `missing_data` (no unit conversion: nothing allocated), `shortfall` (not enough
   * eligible stock; `data.missing`), `expires_before_use` (with a shortfall: stock existed
   * but its best-before is before the use date), `over_reserved` (a row holds less than is
   * claimed on it, e.g. written off after it was reserved), `sold_beyond_stock` (sale
   * variant only).
   */
  flags: Flag[];
}

interface Candidate {
  lot: FefoLot;
  own: Decimal;
  free: Decimal;
}

const ZERO = () => new D(0);
const nonNeg = (d: Decimal) => (d.isNegative() ? ZERO() : d);
const s = (d: Decimal) => qtyToString(qty(d));

function compareNullable<T>(a: T | null, b: T | null, cmp: (x: T, y: T) => number): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  return cmp(a, b);
}

const byText = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * FEFO order: best-before ascending (no best-before last), then produced-at ascending
 * (unknown last), then lot id, then location id and state so the order is total.
 */
export function compareFefo(a: FefoLot, b: FefoLot): number {
  return (
    compareNullable(a.bestBefore, b.bestBefore, byText) ||
    compareNullable(a.producedAt, b.producedAt, (x, y) =>
      Math.sign(toDate(x).getTime() - toDate(y).getTime()),
    ) ||
    byText(a.lotId, b.lotId) ||
    byText(a.locationId, b.locationId) ||
    byText(a.state, b.state)
  );
}

function skipReason(
  lot: FefoLot,
  request: FefoRequest,
  context: FefoContext,
  useDate: PlainDate,
): FefoSkipReason | null {
  const use = context.locations.get(lot.locationId);
  if (!use) return 'location_unknown';
  if (!(request.purpose === 'consumption' ? use.feedsProduction : use.feedsSale)) {
    return 'location_not_allowed';
  }
  if (lot.bestBefore !== null && lot.bestBefore < useDate) return 'expires_before_use';
  const recorded = context.stateRule.findIndex((st) => st.key === lot.state);
  if (recorded < 0) return 'state_unknown';
  // Stock only moves forward through states: judge it by the later of its recorded state
  // and the state the rule gives it on the use date (the daily state pass may lag).
  const derived =
    lot.bestBefore === null
      ? recorded
      : stateOn(lot.bestBefore, request.useAt, context.timeZone, context.stateRule).index;
  const state = context.stateRule[Math.max(recorded, derived)] as StateRuleState;
  const usable = request.purpose === 'consumption' ? state.usableAsIngredient : state.sellable;
  return usable ? null : 'state_not_usable';
}

function emptyResult(requested: string | null, uom: Uom, flags: Flag[] = []): FefoResult {
  return { requested, allocated: '0', uom, allocations: [], skipped: [], flags };
}

/**
 * Allocates a request **first-expired, first-out** (plan §5.6 step 2; vault Core Model).
 *
 * Eligible rows: the request's product; a location that feeds the purpose (production for
 * consumption, sale for a sale); best-before **on or after the local use date** in the
 * account's zone (SAST midnight, not UTC); a state usable for the purpose
 * (`usableAsIngredient` / `sellable`); something available after other reservations.
 * The request's own reservations are drawn first, then free stock, each in
 * {@link compareFefo} order; rows may be used partly.
 *
 * Never allocates an ineligible row and never more than is available: a shortfall
 * allocates what exists and flags the rest. Zero or negative requests allocate nothing.
 * A missing unit conversion allocates nothing and flags `missing_data`.
 */
export function allocateFefo(
  request: FefoRequest,
  lots: readonly FefoLot[],
  context: FefoContext,
): FefoResult {
  const base = context.baseUom;
  const converted = convert(request.qty, request.uom, base, context.conversions ?? [], {
    productId: request.productId,
  });
  if (!converted.ok) {
    return emptyResult(null, base, [
      {
        code: 'missing_data',
        message: `No conversion from ${request.uom} to ${base}.`,
        data: { productId: request.productId, from: request.uom, to: base },
      },
    ]);
  }
  const requested: Decimal = converted.qty;
  if (!requested.isPositive() || requested.isZero()) return emptyResult(s(requested), base);

  const useDate = localDate(request.useAt, context.timeZone);
  const skipped: FefoSkip[] = [];
  const flags: Flag[] = [];
  const expired: FefoLot[] = [];
  const candidates: Candidate[] = [];

  for (const lot of [...lots].sort(compareFefo)) {
    const skip = (reason: FefoSkipReason) =>
      skipped.push({ lotId: lot.lotId, locationId: lot.locationId, state: lot.state, reason });
    if (lot.productId !== request.productId) {
      skip('other_product');
      continue;
    }
    const onHand = qty(lot.qty);
    let own = ZERO();
    let others = ZERO();
    for (const r of lot.reservations ?? []) {
      if (request.reservationId !== undefined && r.reservationId === request.reservationId) {
        own = own.plus(qty(r.qty));
      } else {
        others = others.plus(qty(r.qty));
      }
    }
    if (onHand.lessThan(own.plus(others))) {
      flags.push({
        code: 'over_reserved',
        message: 'Less stock is on hand than is reserved on it.',
        data: {
          lotId: lot.lotId,
          locationId: lot.locationId,
          state: lot.state,
          onHand: s(onHand),
          reserved: s(own.plus(others)),
        },
      });
    }
    const reason = skipReason(lot, request, context, useDate);
    if (reason) {
      skip(reason);
      if (reason === 'expires_before_use' && onHand.isPositive() && !onHand.isZero()) {
        expired.push(lot);
      }
      continue;
    }
    const usable = nonNeg(onHand.minus(others));
    if (usable.isZero()) {
      skip('nothing_available');
      continue;
    }
    const ownPart = own.lessThan(usable) ? own : usable;
    candidates.push({ lot, own: ownPart, free: usable.minus(ownPart) });
  }

  let remaining = requested;
  const taken = new Map<Candidate, { own: Decimal; free: Decimal }>();
  for (const phase of ['own', 'free'] as const) {
    for (const c of candidates) {
      if (remaining.isZero()) break;
      const take = c[phase].lessThan(remaining) ? c[phase] : remaining;
      if (take.isZero()) continue;
      const t = taken.get(c) ?? { own: ZERO(), free: ZERO() };
      t[phase] = t[phase].plus(take);
      taken.set(c, t);
      remaining = remaining.minus(take);
    }
  }

  const allocations: FefoAllocation[] = candidates
    .filter((c) => taken.has(c))
    .map((c) => {
      const t = taken.get(c) as { own: Decimal; free: Decimal };
      return {
        lotId: c.lot.lotId,
        locationId: c.lot.locationId,
        state: c.lot.state,
        bestBefore: c.lot.bestBefore,
        qty: s(t.own.plus(t.free)),
        fromReservation: s(t.own),
      };
    });
  const allocated = requested.minus(remaining);

  if (!remaining.isZero()) {
    flags.push({
      code: 'shortfall',
      message: `Only ${s(allocated)} of ${s(requested)} ${base} could be allocated.`,
      data: {
        productId: request.productId,
        requested: s(requested),
        allocated: s(allocated),
        missing: s(remaining),
        uom: base,
      },
    });
    if (expired.length > 0) {
      flags.push({
        code: 'expires_before_use',
        message: `Stock exists, but its best-before is before ${useDate}.`,
        data: {
          productId: request.productId,
          useDate,
          // Each row by lot, location and state (a lot can be at two places or in two
          // states; lot-less stock shares the lot id '').
          lots: expired.map((l) => ({
            lotId: l.lotId,
            locationId: l.locationId,
            state: l.state,
            bestBefore: l.bestBefore,
            qty: l.qty,
          })),
        },
      });
    }
  }

  return {
    requested: s(requested),
    allocated: s(allocated),
    uom: base,
    allocations,
    skipped,
    flags,
  };
}

/** Options for {@link allocateFefoForSale}. */
export interface SaleAllocationOptions {
  /**
   * When eligible stock runs out, put the rest on the FEFO-first row of the product at a
   * sale-feeding location anyway (whatever its state, date or quantity), driving it
   * negative, and flag `sold_beyond_stock`. Used by the sales replay: a sale is a
   * physical fact, so the units came from somewhere. Default `false`.
   */
  allowNegative?: boolean;
}

/**
 * {@link allocateFefo} for a sale of made-ahead finished goods. With `allowNegative`, a
 * shortfall is placed on the oldest sale-feeding row instead (flag `sold_beyond_stock`
 * replaces `shortfall`); if the product has no such row at all, the shortfall stays.
 */
export function allocateFefoForSale(
  request: Omit<FefoRequest, 'purpose'>,
  lots: readonly FefoLot[],
  context: FefoContext,
  options: SaleAllocationOptions = {},
): FefoResult {
  const result = allocateFefo({ ...request, purpose: 'sale' }, lots, context);
  const shortfall = result.flags.find((f) => f.code === 'shortfall');
  if (!options.allowNegative || !shortfall || result.requested === null) return result;

  const fallback = lots
    .filter(
      (l) => l.productId === request.productId && context.locations.get(l.locationId)?.feedsSale,
    )
    .sort(compareFefo)[0];
  if (!fallback) return result;

  const missing = qty(result.requested).minus(qty(result.allocated));
  const allocations = [...result.allocations];
  const i = allocations.findIndex(
    (a) =>
      a.lotId === fallback.lotId &&
      a.locationId === fallback.locationId &&
      a.state === fallback.state,
  );
  if (i >= 0) {
    const a = allocations[i] as FefoAllocation;
    allocations[i] = { ...a, qty: s(qty(a.qty).plus(missing)) };
  } else {
    allocations.push({
      lotId: fallback.lotId,
      locationId: fallback.locationId,
      state: fallback.state,
      bestBefore: fallback.bestBefore,
      qty: s(missing),
      fromReservation: '0',
    });
  }
  return {
    ...result,
    allocated: result.requested,
    allocations,
    flags: [
      ...result.flags.filter((f) => f !== shortfall),
      {
        code: 'sold_beyond_stock',
        message: `Sold ${s(missing)} ${result.uom} more than was in stock.`,
        data: {
          productId: request.productId,
          lotId: fallback.lotId,
          locationId: fallback.locationId,
          state: fallback.state,
          qty: s(missing),
        },
      },
    ],
  };
}
